import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const D = {
  bg:      '#0a0a09',
  surface: '#111110',
  surface2:'#161614',
  border:  '#2a2927',
  text:    '#f0ede8',
  text2:   '#a8a49e',
  text3:   '#6b6966',
  accent:  '#c49a3c',
  red:     '#e05252',
  redL:    'rgba(224,82,82,0.12)',
  green:   '#4caf7d',
  greenL:  'rgba(76,175,125,0.12)',
};

const inp = {
  padding: '11px 14px', borderRadius: '8px', border: `1px solid ${D.border}`,
  fontSize: '14px', color: D.text, background: D.surface2, outline: 'none',
  fontFamily: "'DM Sans', sans-serif", width: '100%', boxSizing: 'border-box',
};
const ta = { ...inp, resize: 'vertical', minHeight: '80px' };
const lbl = { fontSize: '11px', fontWeight: 600, color: D.text3, textTransform: 'uppercase', letterSpacing: '0.6px' };
const sectionTitle = { fontSize: '11px', fontWeight: 600, color: D.accent, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '16px' };
const divider = { height: '1px', background: D.border, margin: '20px 0' };

function TablaIcon({ size = 32, color = '#c49a3c' }) {
  const cx = size/2, cy = size/2;
  const r1 = size*0.44, r2 = size*0.34, r3 = size*0.18, r4 = size*0.085, dot = size*0.028;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none">
      <ellipse cx={cx} cy={cy} rx={r1} ry={r1} stroke={color} strokeWidth={size*0.05}/>
      <ellipse cx={cx} cy={cy} rx={r2} ry={r2} stroke={color} strokeWidth={size*0.025}
        strokeDasharray={`${size*0.07} ${size*0.055}`} opacity="0.6"/>
      <ellipse cx={cx} cy={cy} rx={r3} ry={r3} fill={color} opacity="0.9"/>
      <ellipse cx={cx} cy={cy} rx={r4} ry={r4} stroke="#0e0e0d" strokeWidth={size*0.02} opacity="0.45"/>
      <circle cx={cx}    cy={cy-r1} r={dot} fill={color} opacity="0.45"/>
      <circle cx={cx}    cy={cy+r1} r={dot} fill={color} opacity="0.45"/>
      <circle cx={cx-r1} cy={cy}    r={dot} fill={color} opacity="0.45"/>
      <circle cx={cx+r1} cy={cy}    r={dot} fill={color} opacity="0.45"/>
    </svg>
  );
}

const EMPTY = {
  full_name: '', email: '', date_of_birth: '', guardian_name: '',
  guardian_phone: '', username: '', password: '', confirm_password: '',
  why_learn: '', goals: '', experience: '',
};

export default function SignUpPage() {
  const navigate = useNavigate();
  const [form,       setForm]       = useState(EMPTY);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success,    setSuccess]    = useState(false);
  const [apiErr,     setApiErr]     = useState('');

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm(f => ({ ...f, [k]: v }));
    setErrors(prev => ({ ...prev, [k]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim())    e.full_name     = 'Full name is required';
    if (!form.email.trim())        e.email         = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email address';
    if (!form.date_of_birth)       e.date_of_birth = 'Date of birth is required';
    if (!form.username.trim())     e.username      = 'Username is required';
    else if (form.username.length < 3) e.username  = 'Min. 3 characters';
    else if (!/^[a-zA-Z0-9_]+$/.test(form.username)) e.username = 'Letters, numbers and _ only';
    if (!form.password)            e.password      = 'Password is required';
    else if (form.password.length < 6) e.password  = 'Min. 6 characters';
    if (form.password !== form.confirm_password) e.confirm_password = 'Passwords do not match';
    if (!form.why_learn.trim())    e.why_learn     = 'Please tell us why you want to learn';
    return e;
  };

  const handleSubmit = async () => {
    setApiErr('');
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSubmitting(true);
    try {
      await axios.post('/api/auth/signup', {
        full_name:      form.full_name.trim(),
        email:          form.email.trim().toLowerCase(),
        date_of_birth:  form.date_of_birth,
        guardian_name:  form.guardian_name.trim() || null,
        guardian_phone: form.guardian_phone.trim() || null,
        username:       form.username.trim().toLowerCase(),
        password:       form.password,
        why_learn:      form.why_learn.trim(),
        goals:          form.goals.trim() || null,
        experience:     form.experience.trim() || null,
      });
      setSuccess(true);
    } catch (err) {
      setApiErr(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) return (
    <div style={{ background: D.bg, minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: "'DM Sans', sans-serif", padding: '20px' }}>
      <div style={{ textAlign: 'center', maxWidth: '480px' }}>
        <div style={{ width: '72px', height: '72px', borderRadius: '50%',
          background: D.greenL, border: `2px solid ${D.green}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px', fontSize: '32px', color: D.green }}>✓</div>
        <h2 style={{ fontSize: '26px', fontWeight: 700, color: D.text, margin: '0 0 12px' }}>Application Submitted!</h2>
        <p style={{ fontSize: '15px', color: D.text2, lineHeight: 1.7, margin: '0 0 8px' }}>
          Thanks, <strong style={{ color: D.text }}>{form.full_name}</strong>! Your application has been sent to the teacher for review.
        </p>
        <p style={{ fontSize: '14px', color: D.text3, lineHeight: 1.7, margin: '0 0 32px' }}>
          You'll receive an email at <strong style={{ color: D.text2 }}>{form.email}</strong> once your account is approved. This usually takes less than 24 hours.
        </p>
        <button onClick={() => navigate('/')}
          style={{ background: D.accent, border: 'none', color: '#0e0e0d',
            padding: '12px 28px', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
            cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
          Back to Home
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ background: D.bg, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif",
      padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '36px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px', cursor: 'pointer' }}
          onClick={() => navigate('/')}>
          <TablaIcon size={48} color={D.accent}/>
        </div>
        <h1 style={{ fontSize: '26px', fontWeight: 700, color: D.text, margin: '0 0 6px' }}>Apply for Access</h1>
        <p style={{ fontSize: '14px', color: D.text3, margin: 0 }}>
          Already have an account?{' '}
          <button onClick={() => navigate('/login')}
            style={{ background: 'none', border: 'none', color: D.accent, cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, padding: 0 }}>
            Log in
          </button>
        </p>
      </div>

      {/* Form card */}
      <div style={{ background: D.surface, border: `1px solid ${D.border}`, borderRadius: '16px',
        padding: 'clamp(16px, 4vw, 32px)', width: '100%', maxWidth: '580px', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>

        {/* ── Personal Info ── */}
        <div style={sectionTitle}>Personal Information</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={lbl}>Full Name <span style={{ color: D.accent }}>*</span></label>
            <input style={{ ...inp, borderColor: errors.full_name ? D.red : D.border }}
              type="text" value={form.full_name} placeholder="e.g. Harmann Singh" onChange={set('full_name')}/>
            {errors.full_name && <span style={{ fontSize: '12px', color: D.red }}>{errors.full_name}</span>}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={lbl}>Email <span style={{ color: D.accent }}>*</span></label>
            <input style={{ ...inp, borderColor: errors.email ? D.red : D.border }}
              type="email" value={form.email} placeholder="your@email.com" onChange={set('email')}/>
            {errors.email && <span style={{ fontSize: '12px', color: D.red }}>{errors.email}</span>}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={lbl}>Date of Birth <span style={{ color: D.accent }}>*</span></label>
            <input style={{ ...inp, borderColor: errors.date_of_birth ? D.red : D.border }}
              type="date" value={form.date_of_birth} onChange={set('date_of_birth')}/>
            {errors.date_of_birth && <span style={{ fontSize: '12px', color: D.red }}>{errors.date_of_birth}</span>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px,100%), 1fr))', gap: '14px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={lbl}>Guardian Name</label>
              <input style={inp} type="text" value={form.guardian_name}
                placeholder="Parent or guardian" onChange={set('guardian_name')}/>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={lbl}>Guardian Phone</label>
              <input style={inp} type="text" value={form.guardian_phone}
                placeholder="+1 (416) 555-0100" onChange={set('guardian_phone')}/>
            </div>
          </div>
        </div>

        <div style={divider}/>

        {/* ── Background ── */}
        <div style={sectionTitle}>Your Background</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={lbl}>Why do you want to learn tabla? <span style={{ color: D.accent }}>*</span></label>
            <textarea style={{ ...ta, borderColor: errors.why_learn ? D.red : D.border }}
              value={form.why_learn}
              placeholder="Tell us what draws you to tabla and why you want to learn..."
              onChange={set('why_learn')}/>
            {errors.why_learn && <span style={{ fontSize: '12px', color: D.red }}>{errors.why_learn}</span>}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={lbl}>What do you expect to achieve?</label>
            <textarea style={ta} value={form.goals}
              placeholder="What are your goals? e.g. perform at events, learn a specific style, personal enjoyment..."
              onChange={set('goals')}/>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={lbl}>How long have you been playing?</label>
            <input style={inp} type="text" value={form.experience}
              placeholder="e.g. Complete beginner, 6 months, 2 years..."
              onChange={set('experience')}/>
          </div>
        </div>

        <div style={divider}/>

        {/* ── Account Details ── */}
        <div style={sectionTitle}>Account Details</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={lbl}>Username <span style={{ color: D.accent }}>*</span></label>
            <input style={{ ...inp, borderColor: errors.username ? D.red : D.border }}
              type="text" value={form.username} placeholder="e.g. harmann" onChange={set('username')}/>
            {errors.username && <span style={{ fontSize: '12px', color: D.red }}>{errors.username}</span>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px,100%), 1fr))', gap: '14px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={lbl}>Password <span style={{ color: D.accent }}>*</span></label>
              <input style={{ ...inp, borderColor: errors.password ? D.red : D.border }}
                type="password" value={form.password} placeholder="Min. 6 characters" onChange={set('password')}/>
              {errors.password && <span style={{ fontSize: '12px', color: D.red }}>{errors.password}</span>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={lbl}>Confirm Password <span style={{ color: D.accent }}>*</span></label>
              <input style={{ ...inp, borderColor: errors.confirm_password ? D.red : D.border }}
                type="password" value={form.confirm_password} placeholder="Repeat password" onChange={set('confirm_password')}/>
              {errors.confirm_password && <span style={{ fontSize: '12px', color: D.red }}>{errors.confirm_password}</span>}
            </div>
          </div>
        </div>

        {apiErr && (
          <div style={{ background: D.redL, border: '1px solid rgba(224,82,82,0.25)',
            borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: D.red, marginTop: '16px' }}>
            {apiErr}
          </div>
        )}

        <button onClick={handleSubmit} disabled={submitting}
          style={{ width: '100%', marginTop: '24px', background: D.accent, border: 'none',
            color: '#0e0e0d', padding: '13px', borderRadius: '8px', fontSize: '15px',
            fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer',
            fontFamily: "'DM Sans', sans-serif", opacity: submitting ? 0.7 : 1 }}>
          {submitting ? 'Submitting...' : 'Submit Application'}
        </button>

        <p style={{ fontSize: '12px', color: D.text3, textAlign: 'center', margin: '16px 0 0', lineHeight: 1.6 }}>
          By applying you agree that your information will be reviewed by the teacher before account activation.
        </p>
      </div>
    </div>
  );
}
